class ApiClient {
  constructor() {
    this.baseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';
    this.unauthorizedHandler = null; // Global handler for 401 responses
  }

  // Method to set the global 401 handler
  setUnauthorizedHandler(handler) {
    this.unauthorizedHandler = handler;
  }

  getAuthToken() {
    return localStorage.getItem('authToken');
  }

  buildUrl(endpoint) {
    // Ensure endpoint starts with a slash
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    
    // If the endpoint already includes /api/, just append to base URL
    if (path.startsWith('/api/')) {
      if (this.baseUrl.endsWith('/')) {
        return `${this.baseUrl.slice(0, -1)}${path}`;
      } else {
        return `${this.baseUrl}${path}`;
      }
    }
    
    // Handle different base URL formats for endpoints without /api/
    if (this.baseUrl.endsWith('/api')) {
      return `${this.baseUrl}${path}`;
    } else if (this.baseUrl.endsWith('/api/')) {
      return `${this.baseUrl.slice(0, -1)}${path}`;
    } else if (this.baseUrl.endsWith('/')) {
      return `${this.baseUrl}api${path}`;
    } else {
      return `${this.baseUrl}/api${path}`;
    }
  }

  buildHeaders(options = {}) {
    const headers = { ...options.headers };
    
    // Add auth token if available and not explicitly excluded
    if (!options.skipAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    // Set content type if not provided and we have JSON data
    if (!headers['Content-Type'] && options.json) {
      headers['Content-Type'] = 'application/json';
    }

    return headers;
  }

  formatErrorDetail(detail, status) {
    if (detail == null || detail === '') {
      return `Request failed (${status}). Please try again.`;
    }
    if (typeof detail === 'object') {
      if (Array.isArray(detail)) {
        return detail.map((item) => item.msg || item.message || String(item)).join(', ');
      }
      return detail.message || detail.msg || JSON.stringify(detail);
    }
    const text = String(detail);
    if (
      text.trim().startsWith('<!DOCTYPE') ||
      text.includes('Bad gateway') ||
      text.includes('cf-error-details')
    ) {
      return `Server error (${status}). Please try again in a moment.`;
    }
    if (text.length > 500) {
      return `Request failed (${status}). Please try again.`;
    }
    return text;
  }

  async handleResponse(response) {
    if (!response.ok) {
      let errorData;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          errorData = { detail: await response.text() || response.statusText };
        }
      } catch (e) {
        errorData = { detail: response.statusText };
      }

      // Handle 401 Unauthorized globally
      if (response.status === 401 && this.unauthorizedHandler) {
        console.log('401 Unauthorized response detected, clearing token and redirecting to login');
        this.unauthorizedHandler();
        // Still throw the error so the calling code can handle it if needed
      }

      const message = this.formatErrorDetail(errorData.detail, response.status);
      const error = new Error(message || `HTTP ${response.status}: ${response.statusText}`);
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null;
    }

    // Try to parse as JSON, fallback to text
    const contentType = response.headers.get('content-type');

    if (contentType && contentType.includes('application/json')) {
      const jsonData = await response.json();
      return jsonData;
    }

    return response;
  }

  async request(endpoint, options = {}) {
    const {
      method = 'GET',
      data = null,
      headers = {},
      skipAuth = false,
      ...fetchOptions
    } = options;

    const url = this.buildUrl(endpoint);
    const requestHeaders = this.buildHeaders({ headers, skipAuth, json: !!data });

    const config = {
      method,
      headers: requestHeaders,
      ...fetchOptions
    };

    // Handle request body
    if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
      if (data instanceof FormData) {
        // Don't set Content-Type for FormData, let browser set it with boundary
        delete config.headers['Content-Type'];
        config.body = data;
      } else if (typeof data === 'string' || data instanceof URLSearchParams) {
        config.body = data;
      } else {
        config.body = JSON.stringify(data);
      }
    }

    // console.log(`Making ${method} request to: ${url}`); // Uncomment for debugging
    
    try {
      const response = await fetch(url, config);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('API Client Error:', error);
      throw error;
    }
  }

  // Convenience methods
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', data });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', data });
  }

  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, { ...options, method: 'PATCH', data });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // File download method
  async downloadFile(endpoint, options = {}) {
    const url = this.buildUrl(endpoint);
    const headers = this.buildHeaders(options);

    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    
    return response;
  }
}

// Create and export a singleton instance
const apiClient = new ApiClient();

// Export both the instance and the class for flexibility
export default apiClient;
export { ApiClient }; 